import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Chip from '@material-ui/core/Chip';
import { getCustomerDetail } from '../../../services/CustomerService';
import { updateOrderDeliveryDetails } from '../../../services/CustomerService';

const SECONDARY_FIELDS = [
  { key: 'company', label: 'Company name' },
  { key: 'suite', label: 'Suite / Unit' },
  { key: 'door_code', label: 'Door code' },
  { key: 'building_name', label: 'Building name' },
  { key: 'subdivision', label: 'Subdivision' },
  { key: 'additional_instructions', label: 'Delivery instructions' },
  { key: 'label', label: 'Address label (e.g. Home, Office)' },
];

const formatAddr = (addr) =>
  [addr.address1, addr.city, addr.state, addr.zip].filter(Boolean).join(', ');

export default function EditAddressDialog({
  open,
  onClose,
  orderId,
  customerId,
  currentAddress,
  onSuccess,
  onError,
}) {
  const [tab, setTab] = useState(0);
  const [fields, setFields] = useState({});
  const [initialFields, setInitialFields] = useState({});
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTab(0);
      const prefill = {};
      if (currentAddress) {
        SECONDARY_FIELDS.forEach(({ key }) => {
          if (currentAddress[key]) prefill[key] = currentAddress[key];
        });
      }
      setFields(prefill);
      setInitialFields(prefill);
      setSelectedAddressId('');
      setAddresses([]);
    }
  }, [open, currentAddress]);

  useEffect(() => {
    if (open && tab === 1 && addresses.length === 0 && customerId) {
      setLoadingAddresses(true);
      getCustomerDetail(customerId)
        .then((detail) => {
          setAddresses(detail?.addresses || []);
        })
        .catch(() => {
          setAddresses([]);
        })
        .finally(() => setLoadingAddresses(false));
    }
  }, [open, tab, customerId, addresses.length]);

  const handleFieldChange = (key, value) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let payload;
      if (tab === 0) {
        const changed = {};
        Object.entries(fields).forEach(([k, v]) => {
          const trimmed = v ? v.trim() : '';
          const initial = initialFields[k] || '';
          if (trimmed !== initial) changed[k] = trimmed;
        });
        payload = { address: changed };
      } else {
        payload = { address: { address_id: Number(selectedAddressId) } };
      }
      await updateOrderDeliveryDetails(orderId, payload);
      onSuccess('Address updated successfully.');
      onClose();
    } catch (err) {
      onError(err?.message || 'Failed to update address.');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = Object.entries(fields).some(([k, v]) => {
    const trimmed = v ? v.trim() : '';
    const initial = initialFields[k] || '';
    return trimmed !== initial;
  });
  const canSave = tab === 0 ? hasChanges : !!selectedAddressId;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Delivery Address</DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Edit details" />
          <Tab label="Use saved address" disabled={!customerId} />
        </Tabs>

        <Box mt={2}>
          {tab === 0 && (
            <>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Add or correct secondary address information. Primary address
                fields (street, city, state, zip) cannot be changed here — use
                "Use saved address" to swap to a different address.
              </Typography>
              {SECONDARY_FIELDS.map(({ key, label }) => (
                <Box key={key} mb={1.5}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label={label}
                    value={fields[key] || ''}
                    onChange={(e) => handleFieldChange(key, e.target.value)}
                    multiline={key === 'additional_instructions'}
                    rows={key === 'additional_instructions' ? 2 : 1}
                  />
                </Box>
              ))}
            </>
          )}

          {tab === 1 && (
            <>
              {loadingAddresses ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : (() => {
                const currentId = currentAddress?.id;
                const otherAddresses = currentId
                  ? addresses.filter((a) => a.id !== currentId)
                  : addresses;
                return otherAddresses.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No other saved addresses available.
                  </Typography>
                ) : (
                  <>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Select a different address from the customer's profile.
                    </Typography>
                    <RadioGroup
                      value={selectedAddressId}
                      onChange={(e) => setSelectedAddressId(e.target.value)}
                    >
                      {otherAddresses.map((addr) => (
                        <FormControlLabel
                          key={addr.id}
                          value={String(addr.id)}
                          control={<Radio size="small" color="primary" />}
                          label={
                            <Box>
                              <Typography variant="body2">
                                {formatAddr(addr)}
                              </Typography>
                              {!addr.isActive && (
                                <Chip
                                  label="Inactive"
                                  size="small"
                                  style={{ height: 18, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </>
                );
              })()}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={!canSave || saving}
        >
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EditAddressDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  orderId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  customerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currentAddress: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

EditAddressDialog.defaultProps = {
  customerId: null,
  currentAddress: null,
};
